import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { createPortal } from "react-dom";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { BsInfoCircle } from "react-icons/bs";
import { EmbedFormBtn } from "./embedFormButtons";
import {
    EMBED_BIRTHDAY_FORMAT_HINT,
    attachEmbedBirthdayFieldGuards,
    attachEmbedBirthdayPickerSync,
} from "../../../embedBirthdayField";
import { sanitizeEmbedNumberFieldValue } from "../../../embedNumberInput";
import { EmbedSuccessCheckmark } from "../../../embedSuccessCheckmark";
import {
    buildEmbedFloatingLauncherPreviewCss,
    buildEmbedThemeCss,
    EMBED_DEFAULT_TEXT_COLOR,
    embedChoiceOptionsLayoutClass,
    embedFieldChrome,
    embedLauncherHoverText,
    embedLauncherIconHtml,
    clearEmbedStageSuccessHeight,
    resolveEmbedSuccessMessage,
    resolveEmbedSuccessDisclaimer,
    resolveEmbedSupportContact,
    normalizeEmbedDisplayMode,
    normalizeEmbedLauncherPosition,
    resolveEmbedFloatingPanelWidth,
    resolveEmbedButtonBg,
    resolveEmbedLauncherBg,
    pinEmbedStageHeightForSuccess,
    resolveSuccessThemeFromCfg,
    unlockEmbedCardWidth,
} from "../../../embedTicketFormTheme";
import "./embed-form-editor.css";

/** Live preview + widget: bare numbers become px; empty → full width */
export function normalizeFormMaxWidth(raw) {
    const s = String(raw ?? "").trim();
    if (!s) return "100%";
    if (/^\d+(\.\d+)?$/.test(s)) return `${s}px`;
    return s;
}

/** Bare numbers → px; empty → no min-width rule in theme (via ""). */
export function normalizeFormMinWidth(raw) {
    const s = String(raw ?? "").trim();
    if (!s) return "";
    if (/^\d+(\.\d+)?$/.test(s)) return `${s}px`;
    return s;
}

/** @returns {number|null} pixel length for bare numbers / `Npx` only */
export function parseNumericPxFromEmbedWidth(raw) {
    const s = String(raw ?? "").trim();
    if (!s) return null;
    if (/^\d+(\.\d+)?$/.test(s)) return parseFloat(s);
    const m = /^(\d+(?:\.\d+)?)px$/i.exec(s);
    if (m) return parseFloat(m[1]);
    return null;
}

function EmbedRequiredMark() {
    return (
        <span className="claritick-ticket-embed-required-star" aria-hidden="true">
            {" *"}
        </span>
    );
}

function BirthdayPreviewField({ label, required }) {
    const textRef = useRef(null);
    const pickerRef = useRef(null);
    const pickerBtnRef = useRef(null);

    useLayoutEffect(() => {
        const text = textRef.current;
        const picker = pickerRef.current;
        const btn = pickerBtnRef.current;
        if (!text || !picker || !btn) {
            return undefined;
        }
        attachEmbedBirthdayFieldGuards(text);
        return attachEmbedBirthdayPickerSync(text, picker, btn);
    }, []);

    return (
        <label className="claritick-ticket-embed-label">
            <span>
                {label}
                {required ? <EmbedRequiredMark /> : null}
            </span>
            <div className="claritick-ticket-embed-birthday-wrap">
                <input
                    ref={textRef}
                    type="text"
                    inputMode="numeric"
                    autoComplete="bday"
                    placeholder={EMBED_BIRTHDAY_FORMAT_HINT}
                    maxLength={10}
                    pattern="\d{2}-\d{2}-\d{4}"
                    title="Enter date as DD-MM-YYYY or use the calendar"
                    required={required}
                    className="claritick-ticket-embed-field claritick-ticket-embed-field--birthday"
                />
                <button
                    ref={pickerBtnRef}
                    type="button"
                    className="claritick-ticket-embed-birthday-picker-btn"
                    aria-label="Open calendar"
                >
                    <svg
                        className="claritick-ticket-embed-birthday-picker-btn__icon"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden
                    >
                        <rect x="3" y="4" width="18" height="18" rx="2" />
                        <path d="M16 2v4M8 2v4M3 10h18" />
                    </svg>
                </button>
                <input
                    ref={pickerRef}
                    type="date"
                    lang="en-GB"
                    tabIndex={-1}
                    className="claritick-ticket-embed-birthday-picker-native"
                    aria-hidden
                />
            </div>
        </label>
    );
}

function selectEmptyOptionLabel(placeholder) {
    const s = String(placeholder ?? "").trim();
    return s || "—";
}

function parseHexColor(hex) {
    if (typeof hex !== "string") return null;
    let s = hex.trim();
    if (/^#[0-9A-Fa-f]{3}$/.test(s)) {
        s =
            "#" +
            s[1] +
            s[1] +
            s[2] +
            s[2] +
            s[3] +
            s[3];
    }
    if (!/^#[0-9A-Fa-f]{6}$/.test(s)) return null;
    return {
        r: parseInt(s.slice(1, 3), 16),
        g: parseInt(s.slice(3, 5), 16),
        b: parseInt(s.slice(5, 7), 16),
    };
}

function relativeLuminance({ r, g, b }) {
    const lin = [r, g, b].map((c) => {
        const v = c / 255;
        return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
}

function resolvedButtonTextColor(buttonBgCss, explicitHex) {
    const ex = explicitHex && String(explicitHex).trim();
    if (ex) return ex;
    const rgb = parseHexColor(buttonBgCss);
    if (!rgb) return "#ffffff";
    return relativeLuminance(rgb) > 0.45 ? "#111111" : "#ffffff";
}

function previewSuccessMessage(cfg) {
    return resolveEmbedSuccessMessage(cfg);
}

/** Same rules as embed-ticket-form.js for required checkbox groups. */
function validatePreviewRequiredCheckboxes(formEl, customFields) {
    const fields = Array.isArray(customFields) ? customFields : [];
    for (let idx = 0; idx < fields.length; idx++) {
        const field = fields[idx];
        if (field.type !== "checkbox" || !field.required) {
            continue;
        }
        const options = (field.options || []).filter(
            (o) => String(o).trim() !== ""
        );
        if (options.length === 0) {
            continue;
        }
        const groupName = `pv-${field.name || "f"}-${idx}`;
        const checked = formEl.querySelectorAll(
            `input[name="${groupName}"]:checked`
        );
        if (checked.length === 0) {
            return (
                "Please select at least one option for: " +
                (field.label || field.name)
            );
        }
    }
    return null;
}

/**
 * Live preview of the public embed (same structure / look as embed-ticket-form.js).
 *
 * Wrapped in `React.memo` so unrelated re-renders in the editor (e.g. typing in inputs
 * that don't change `cfg`) don't trigger the heavy CSS-string rebuild here.
 *
 * @param {{ cfg: Record<string, unknown>, footerHint?: string | null, showAfterSubmitPreview?: boolean, allowViewToggle?: boolean, designPreviewView?: 'form'|'success', onDesignPreviewViewChange?: (v: 'form'|'success') => void, openSuccessPreviewModal?: boolean, onSuccessPreviewModalClose?: () => void }} props
 */
function EmbedFormEditorPreviewImpl({
    cfg,
    footerHint,
    showAfterSubmitPreview = false,
    allowViewToggle = false,
    designPreviewView = "form",
    onDesignPreviewViewChange,
    openSuccessPreviewModal = false,
    onSuccessPreviewModalClose,
}) {
    const measureRef = useRef(null);
    const formRef = useRef(null);
    const cardRef = useRef(null);
    const [panelW, setPanelW] = useState(0);
    const [previewSubmitted, setPreviewSubmitted] = useState(false);
    const [previewError, setPreviewError] = useState(null);
    /** Bumped to remount the checklist SVG so SMIL runs again (img cache ignores remount alone). */
    const [checklistPlayKey, setChecklistPlayKey] = useState(0);
    const [floatingPreviewOpen, setFloatingPreviewOpen] = useState(false);
    /** Form card height — always drives floating preview measure min-height (launcher stays pinned). */
    const [floatingFormH, setFloatingFormH] = useState(0);
    const isFloatingPreview =
        normalizeEmbedDisplayMode(cfg.display_mode) === "floating";
    const launcherPosition = normalizeEmbedLauncherPosition(
        cfg.launcher_position
    );
    /** Success view in the sidebar (wizard / design toggle). */
    const showSuccessInline =
        showAfterSubmitPreview ||
        (allowViewToggle && designPreviewView === "success");

    /** Inline success in the sidebar (pinned step/toggle or live submit on other steps). */
    const showSidebarSuccess =
        showSuccessInline ||
        (previewSubmitted && !showAfterSubmitPreview);

    /** Fullscreen modal — After submit step Preview button only. */
    const showSuccessModal = openSuccessPreviewModal;

    const showSuccess = showSidebarSuccess || showSuccessModal;

    /** Success view is forced (wizard / design toggle) — not a live submit simulation. */
    const pinnedSuccessPreview = showSuccessInline;

    /** Live submit animation (not pinned After submit / design success view). */
    const previewSubmitTransition =
        previewSubmitted && !pinnedSuccessPreview;

    const bumpChecklistAnimation = () => {
        setChecklistPlayKey((k) => k + 1);
    };

    useEffect(() => {
        if (!showAfterSubmitPreview) {
            return;
        }
        setPreviewSubmitted(false);
        setPreviewError(null);
        setChecklistPlayKey((k) => k + 1);
        clearEmbedStageSuccessHeight(
            cardRef.current?.querySelector(".claritick-ticket-embed-stage")
        );
        unlockEmbedCardWidth(cardRef.current);
    }, [showAfterSubmitPreview]);

    useEffect(() => {
        if (openSuccessPreviewModal) {
            setChecklistPlayKey((k) => k + 1);
        }
    }, [openSuccessPreviewModal]);

    /** Editor only: show the floating panel open so builders see the form; the public widget stays closed until click. */
    useEffect(() => {
        if (!isFloatingPreview) {
            setFloatingPreviewOpen(false);
            return;
        }
        setFloatingPreviewOpen(true);
    }, [isFloatingPreview]);

    useEffect(() => {
        if (!allowViewToggle) {
            return;
        }
        if (designPreviewView === "success") {
            setChecklistPlayKey((k) => k + 1);
            return;
        }
        setPreviewSubmitted(false);
        setPreviewError(null);
        clearEmbedStageSuccessHeight(
            cardRef.current?.querySelector(".claritick-ticket-embed-stage")
        );
        unlockEmbedCardWidth(cardRef.current);
    }, [allowViewToggle, designPreviewView]);

    useLayoutEffect(() => {
        if (!showSuccess) {
            clearEmbedStageSuccessHeight(
                cardRef.current?.querySelector(
                    ".claritick-ticket-embed-stage"
                )
            );
            if (!previewSubmitted && !showAfterSubmitPreview) {
                unlockEmbedCardWidth(cardRef.current);
            }
            return;
        }
        if (showSuccessModal) {
            return;
        }
        if (pinnedSuccessPreview && !previewSubmitted) {
            clearEmbedStageSuccessHeight(
                cardRef.current?.querySelector(".claritick-ticket-embed-stage")
            );
            return;
        }
        const stage = cardRef.current?.querySelector(
            ".claritick-ticket-embed-stage"
        );
        if (isFloatingPreview) {
            clearEmbedStageSuccessHeight(stage);
            return;
        }
        pinEmbedStageHeightForSuccess(stage);
    }, [
        showSuccess,
        showSuccessModal,
        showAfterSubmitPreview,
        previewSubmitted,
        isFloatingPreview,
    ]);

    const handlePreviewSubmit = (ev) => {
        if (ev) {
            ev.preventDefault();
            ev.stopPropagation();
        }
        const form = formRef.current;
        if (!form) {
            return;
        }
        setPreviewSubmitted(false);
        setPreviewError(null);

        const checkboxMsg = validatePreviewRequiredCheckboxes(
            form,
            cfg.custom_fields
        );
        if (checkboxMsg) {
            setPreviewError(checkboxMsg);
            return;
        }

        if (!form.checkValidity()) {
            form.reportValidity();
            return;
        }

        bumpChecklistAnimation();
        if (!isFloatingPreview) {
            pinEmbedStageHeightForSuccess(
                cardRef.current?.querySelector(".claritick-ticket-embed-stage")
            );
        }
        setPreviewSubmitted(true);
    };

    const dismissPreviewSuccess = useCallback(() => {
        if (openSuccessPreviewModal) {
            onSuccessPreviewModalClose?.();
            return;
        }

        formRef.current?.reset();
        setPreviewError(null);

        if (pinnedSuccessPreview) {
            /** Replay checklist only — do not change design colors or card chrome. */
            bumpChecklistAnimation();
            return;
        }

        clearEmbedStageSuccessHeight(
            cardRef.current?.querySelector(".claritick-ticket-embed-stage")
        );
        unlockEmbedCardWidth(cardRef.current);
        setPreviewSubmitted(false);
    }, [openSuccessPreviewModal, onSuccessPreviewModalClose, pinnedSuccessPreview]);

    useEffect(() => {
        if (!showSuccessModal) {
            return;
        }
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        document.body.classList.add("embed-form-editor-preview-modal-open");
        const onKeyDown = (e) => {
            if (e.key === "Escape") {
                dismissPreviewSuccess();
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => {
            document.body.style.overflow = prevOverflow;
            document.body.classList.remove(
                "embed-form-editor-preview-modal-open"
            );
            window.removeEventListener("keydown", onKeyDown);
        };
    }, [showSuccessModal, dismissPreviewSuccess]);

    const handlePreviewFormInput = () => {
        if (previewError) {
            setPreviewError(null);
        }
        if (previewSubmitted && !pinnedSuccessPreview) {
            unlockEmbedCardWidth(cardRef.current);
            setPreviewSubmitted(false);
        }
    };

    useLayoutEffect(() => {
        const inner = measureRef.current;
        if (!inner) {
            return;
        }
        /** Observe the preview panel (parent of the measure strip) — column width, not the measure strip. */
        const bounds = inner.parentElement ?? inner;
        const measure = () => {
            setPanelW(
                Math.max(0, Math.floor(bounds.getBoundingClientRect().width))
            );
        };
        measure();
        if (typeof ResizeObserver === "undefined") {
            window.addEventListener("resize", measure);
            return () => window.removeEventListener("resize", measure);
        }
        const ro = new ResizeObserver(() => measure());
        ro.observe(bounds);
        return () => ro.disconnect();
    }, []);

    const customFieldsMeasureKey = useMemo(
        () => JSON.stringify(cfg.custom_fields ?? []),
        [cfg.custom_fields]
    );

    useLayoutEffect(() => {
        if (!isFloatingPreview) {
            setFloatingFormH(0);
            return;
        }
        const card = cardRef.current;
        if (!card) {
            return;
        }
        const measureFormH = () => {
            setFloatingFormH(
                Math.max(0, Math.ceil(card.getBoundingClientRect().height))
            );
        };
        measureFormH();
        if (typeof ResizeObserver === "undefined") {
            window.addEventListener("resize", measureFormH);
            return () => window.removeEventListener("resize", measureFormH);
        }
        const ro = new ResizeObserver(measureFormH);
        ro.observe(card);
        return () => ro.disconnect();
    }, [
        isFloatingPreview,
        customFieldsMeasureKey,
        cfg.title,
        cfg.subtitle,
        showSuccess,
        previewSubmitted,
        showAfterSubmitPreview,
        allowViewToggle,
        designPreviewView,
    ]);

    const instanceId = useMemo(
        () => "pv-" + Math.random().toString(36).slice(2, 11),
        []
    );
    const modalInstanceId = `${instanceId}-modal`;
    const scopeSel = `[data-claritick-instance="${instanceId}"]`;
    const modalScopeSel = `[data-claritick-instance="${modalInstanceId}"]`;

    const maxW = normalizeFormMaxWidth(cfg.form_max_width);
    const minWNormalized = normalizeFormMinWidth(cfg.form_min_width);
    const minPxVal = parseNumericPxFromEmbedWidth(cfg.form_min_width);

    const floatingPanelSizing = useMemo(
        () =>
            resolveEmbedFloatingPanelWidth(
                cfg.form_max_width,
                cfg.form_min_width
            ),
        [cfg.form_max_width, cfg.form_min_width]
    );

    /** Clamp floating panel to the live-preview column (not 100vw) so it does not cross the wizard divider. */
    const floatingPanelEffectivePx = useMemo(() => {
        if (!isFloatingPreview) {
            return floatingPanelSizing.targetPx;
        }
        const pad = 24;
        const cap =
            panelW > 0 ? Math.max(1, Math.floor(panelW - pad)) : null;
        if (cap == null) {
            return floatingPanelSizing.targetPx;
        }
        return Math.min(floatingPanelSizing.targetPx, cap);
    }, [
        isFloatingPreview,
        floatingPanelSizing.targetPx,
        panelW,
    ]);

    const floatingPanelClampedInPreview =
        isFloatingPreview &&
        floatingPanelSizing.targetPx > floatingPanelEffectivePx;

    const previewWidthCapPx = useMemo(() => {
        if (isFloatingPreview) {
            return floatingPanelEffectivePx;
        }
        if (panelW > 0) {
            return Math.max(1, panelW);
        }
        return null;
    }, [isFloatingPreview, floatingPanelEffectivePx, panelW]);

    const minWForTheme = useMemo(() => {
        if (!minWNormalized) {
            return "";
        }
        if (previewWidthCapPx == null || minPxVal == null) {
            return minWNormalized;
        }
        if (minPxVal <= previewWidthCapPx) {
            return minWNormalized;
        }
        return `${previewWidthCapPx}px`;
    }, [minWNormalized, minPxVal, previewWidthCapPx]);

    const minWidthClampedInPreview =
        minPxVal != null &&
        minWNormalized !== "" &&
        previewWidthCapPx != null &&
        minPxVal > previewWidthCapPx;

    const showMinWidthNotice =
        minWidthClampedInPreview || floatingPanelClampedInPreview;

    const minWidthNoticeText = showMinWidthNotice
        ? "This preview area is narrower than the space your form will have on your website. The live embed will still use the min and max width you set."
        : "";

    const bg = cfg.background_color || "#ffffff";
    const textHex = cfg.text_color || EMBED_DEFAULT_TEXT_COLOR;
    const btnBg = resolveEmbedButtonBg(cfg.button_color);
    const btnFg = resolvedButtonTextColor(btnBg, cfg.button_text_color);
    const fbg =
        cfg.field_background_color != null &&
        String(cfg.field_background_color).trim() !== ""
            ? String(cfg.field_background_color).trim()
            : null;

    const titleAlign = cfg.title_align ?? "left";
    const subtitleAlign = cfg.subtitle_align ?? "left";

    const assetOrigin =
        typeof window !== "undefined" && window.location?.origin
            ? window.location.origin
            : "";

    const baseCss = useMemo(
        () => {
            const chromeBase = embedFieldChrome(textHex, bg);
            const chrome = fbg
                ? { border: chromeBase.border, inputBg: fbg }
                : chromeBase;
            let css = buildEmbedThemeCss(
                scopeSel,
                maxW,
                textHex,
                bg,
                chrome,
                btnBg,
                btnFg,
                cfg.label_align ?? "left",
                cfg.title_color,
                cfg.subtitle_color,
                minWForTheme,
                titleAlign,
                subtitleAlign,
                resolveSuccessThemeFromCfg(cfg, assetOrigin),
                cfg.button_color,
                cfg.form_border_radius,
                { keepSuccessCardChrome: true }
            );
            if (isFloatingPreview) {
                const launcherBg = resolveEmbedLauncherBg(
                    cfg.launcher_color,
                    cfg.button_color
                );
                const launcherFg = resolvedButtonTextColor(
                    launcherBg,
                    cfg.button_text_color
                );
                css += buildEmbedFloatingLauncherPreviewCss(
                    scopeSel,
                    launcherBg,
                    launcherFg
                );
            }
            return css;
        },
        [
            assetOrigin,
            isFloatingPreview,
            launcherPosition,
            scopeSel,
            maxW,
            textHex,
            bg,
            fbg,
            btnBg,
            btnFg,
            cfg.label_align,
            cfg.title_color,
            cfg.subtitle_color,
            // success_message_color intentionally omitted — applied inline on the
            // success message so live color edits don't regenerate the stylesheet.
            cfg.success_message_font_family,
            cfg.success_message_font_size,
            cfg.success_message_font_weight,
            cfg.button_color,
            cfg.button_text_color,
            cfg.launcher_color,
            cfg.form_border_radius,
            minWForTheme,
            titleAlign,
            subtitleAlign,
        ]
    );

    const modalBaseCss = useMemo(() => {
        const chromeBase = embedFieldChrome(textHex, bg);
        const chrome = fbg
            ? { border: chromeBase.border, inputBg: fbg }
            : chromeBase;
        let css = buildEmbedThemeCss(
            modalScopeSel,
            "100%",
            textHex,
            bg,
            chrome,
            btnBg,
            btnFg,
            cfg.label_align ?? "left",
            cfg.title_color,
            cfg.subtitle_color,
            "",
            titleAlign,
            subtitleAlign,
            resolveSuccessThemeFromCfg(cfg, assetOrigin),
            cfg.button_color,
            cfg.form_border_radius,
            { keepSuccessCardChrome: true }
        );
        css +=
            modalScopeSel +
            "{display:block;width:100%;max-width:100%;box-sizing:border-box;}" +
            modalScopeSel +
            " :where(.claritick-ticket-embed-card){min-width:0!important;max-width:100%!important;width:100%;}" +
            modalScopeSel +
            " :where(.claritick-ticket-embed-card.is-success-state){padding:20px 22px 24px;}" +
            modalScopeSel +
            " :where(.claritick-ticket-embed-card.is-success-state .claritick-ticket-embed-stage){min-height:0!important;}" +
            modalScopeSel +
            " :where(.claritick-ticket-embed-card.is-success-state .claritick-ticket-embed-stage__body){min-height:0;}";
        return css;
    }, [
        assetOrigin,
        modalScopeSel,
        textHex,
        bg,
        fbg,
        btnBg,
        btnFg,
        cfg.label_align,
        cfg.title_color,
        cfg.subtitle_color,
        // success_message_color intentionally omitted — applied inline on the
        // success message so live color edits don't regenerate the stylesheet.
        cfg.success_message_font_family,
        cfg.success_message_font_size,
        cfg.success_message_font_weight,
        cfg.button_color,
        cfg.form_border_radius,
        titleAlign,
        subtitleAlign,
    ]);

    const hint =
        footerHint === undefined
            ? showSuccess && (showAfterSubmitPreview || allowViewToggle)
                ? null
                : isFloatingPreview
                  ? "Floating mode — click the round button in the corner to open the form preview."
                  : allowViewToggle
                    ? "Use the buttons below to switch between the form and after-submit preview."
                    : "Preview only — fill required fields and submit to see the success message (nothing is sent)"
            : footerHint;

    const setDesignPreviewView = (view) => {
        if (onDesignPreviewViewChange) {
            onDesignPreviewViewChange(view);
        }
    };

    const customCssRaw =
        typeof cfg.custom_css === "string" ? cfg.custom_css : "";

    const floatingShellClass =
        "claritick-ticket-embed-floating claritick-ticket-embed-floating--" +
        launcherPosition +
        " embed-form-editor-preview-floating" +
        (floatingPreviewOpen ? " is-open" : "");

    const floatingMeasureClass =
        "embed-form-editor-preview-measure" +
        (isFloatingPreview
            ? " embed-form-editor-preview-measure--floating embed-form-editor-preview-measure--" +
              launcherPosition
            : "") +
        (isFloatingPreview && floatingPreviewOpen ? " is-floating-open" : "");

    const floatingMeasureStyle = isFloatingPreview
        ? {
              ["--embed-preview-floating-form-h"]: `${floatingFormH}px`,
              ["--embed-floating-panel-target"]: `${floatingPanelSizing.targetPx}px`,
              ["--embed-floating-panel-effective"]: `${floatingPanelEffectivePx}px`,
              ...(floatingPanelSizing.minPx != null
                  ? {
                        ["--embed-floating-panel-min"]: `${floatingPanelSizing.minPx}px`,
                    }
                  : {}),
          }
        : undefined;

    const renderPreviewSuccessStage = (successActive) => {
      // Apply the success color inline so live color edits only touch this element
      // instead of regenerating/re-injecting the whole theme stylesheet (which also
      // replays @font-face and the checkmark animation). Resolved value always wins,
      // so clearing the field correctly falls back to the default color.
      const successColor = resolveSuccessThemeFromCfg(cfg, assetOrigin).color;
      return (
        <div
            className={
                "claritick-ticket-embed-stage__success" +
                (successActive ? " is-visible" : "")
            }
            aria-live="polite"
        >
            {successActive ? (
                <EmbedSuccessCheckmark
                    playKey={checklistPlayKey}
                    assetOrigin={assetOrigin}
                />
            ) : null}
            <p
                className="claritick-ticket-embed-status claritick-ticket-embed-status--success claritick-ticket-embed-success-message"
                style={{ color: successColor }}
            >
                {previewSuccessMessage(cfg)}
            </p>
            {successActive && resolveEmbedSuccessDisclaimer(cfg) ? (
                <p
                    className="claritick-ticket-embed-success-disclaimer"
                    style={{ color: successColor }}
                >
                    {resolveEmbedSuccessDisclaimer(cfg)}
                </p>
            ) : null}
            {successActive
                ? (() => {
                      const contact = resolveEmbedSupportContact(cfg);
                      if (!contact) {
                          return null;
                      }
                      return (
                          <p className="claritick-ticket-embed-success-contact">
                              <a
                                  href={contact.href}
                                  {...(contact.external
                                      ? {
                                            target: "_blank",
                                            rel: "noopener noreferrer",
                                        }
                                      : {})}
                              >
                                  {contact.label}
                              </a>
                          </p>
                      );
                  })()
                : null}
            {successActive ? (
                <Button
                    type="button"
                    variant={EmbedFormBtn.primary}
                    className="embed-form-editor-preview-ui-btn embed-form-editor-preview-track-btn"
                    title="Preview only — not clickable"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                    }}
                >
                    Track your request
                </Button>
            ) : null}
        </div>
      );
    };

    const renderPreviewFormHeader = () =>
        cfg.title || cfg.subtitle ? (
            <div className="claritick-ticket-embed-stage__header">
                {cfg.title ? (
                    <h3 className="claritick-ticket-embed-title">{cfg.title}</h3>
                ) : null}
                {cfg.subtitle ? (
                    <p className="claritick-ticket-embed-subtitle">
                        {cfg.subtitle}
                    </p>
                ) : null}
            </div>
        ) : null;

    const minWidthNoticeButton = showMinWidthNotice ? (
        <OverlayTrigger
            placement={isFloatingPreview ? "bottom" : "left"}
            overlay={
                <Tooltip id={`${instanceId}-min-width`}>
                    {minWidthNoticeText}
                </Tooltip>
            }
        >
            <button
                type="button"
                className={
                    "embed-form-editor-preview__min-width-info" +
                    (isFloatingPreview
                        ? " embed-form-editor-preview__min-width-info--toolbar"
                        : "")
                }
                aria-label={minWidthNoticeText}
            >
                <BsInfoCircle aria-hidden size={16} />
            </button>
        </OverlayTrigger>
    ) : null;

    const previewHostInner = (
        <>
            <style type="text/css">{baseCss}</style>
            {customCssRaw.trim() ? (
                <style type="text/css">{customCssRaw}</style>
            ) : null}
                <div
                    ref={cardRef}
                    className={
                        "embed-form-editor-preview__frame claritick-ticket-embed-card" +
                        (showSidebarSuccess ? " is-success-state" : "") +
                        (isFloatingPreview && showSuccessInline
                            ? " is-success-compact"
                            : "")
                    }
                >
                {!isFloatingPreview ? minWidthNoticeButton : null}
                <div className="claritick-ticket-embed-stage">
                    {!showSidebarSuccess ? renderPreviewFormHeader() : null}
                    <div className="claritick-ticket-embed-stage__body">
                    <div
                        className={
                            "claritick-ticket-embed-stage__form" +
                            (previewSubmitTransition
                                ? " is-exiting" +
                                  (isFloatingPreview ? " is-concealed" : "")
                                : "") +
                            (showSuccessInline &&
                            (showAfterSubmitPreview || allowViewToggle)
                                ? " is-concealed"
                                : "")
                        }
                    >
                <form
                    ref={formRef}
                    className="claritick-ticket-embed-form"
                    autoComplete="off"
                    noValidate
                    onSubmit={handlePreviewSubmit}
                    onInput={handlePreviewFormInput}
                >
                    <label className="claritick-ticket-embed-label">
                        <span>
                            Full name
                            <EmbedRequiredMark />
                        </span>
                        <input
                            type="text"
                            name="_preview_full_name"
                            placeholder="Type here…"
                            required
                            className="claritick-ticket-embed-field claritick-ticket-embed-field--text"
                        />
                    </label>
                    <label className="claritick-ticket-embed-label">
                        <span>
                            Email
                            <EmbedRequiredMark />
                        </span>
                        <input
                            type="email"
                            name="_preview_email"
                            placeholder="you@example.com"
                            required
                            className="claritick-ticket-embed-field claritick-ticket-embed-field--email"
                        />
                    </label>
                    <label className="claritick-ticket-embed-label">
                        <span>
                            Message
                            <EmbedRequiredMark />
                        </span>
                        <textarea
                            rows={4}
                            name="_preview_message"
                            placeholder="Type here…"
                            required
                            className="claritick-ticket-embed-field claritick-ticket-embed-field--textarea"
                        />
                    </label>

                    {(cfg.custom_fields || []).map((field, idx) => {
                        const req = !!field.required;
                        const key = `${field.name || "f"}-${idx}`;

                        if (field.type === "textarea") {
                            return (
                                <label
                                    key={key}
                                    className="claritick-ticket-embed-label"
                                >
                                    <span>
                                        {field.label || field.name}
                                        {req ? <EmbedRequiredMark /> : null}
                                    </span>
                                    <textarea
                                        rows={3}
                                        placeholder={field.placeholder}
                                        required={req}
                                        className="claritick-ticket-embed-field claritick-ticket-embed-field--textarea"
                                    />
                                </label>
                            );
                        }

                        if (field.type === "birthday") {
                            return (
                                <BirthdayPreviewField
                                    key={key}
                                    label={field.label || field.name}
                                    required={req}
                                />
                            );
                        }

                        if (field.type === "number") {
                            return (
                                <label
                                    key={key}
                                    className="claritick-ticket-embed-label"
                                >
                                    <span>
                                        {field.label || field.name}
                                        {req ? <EmbedRequiredMark /> : null}
                                    </span>
                                    <input
                                        type="number"
                                        placeholder={field.placeholder}
                                        required={req}
                                        className="claritick-ticket-embed-field claritick-ticket-embed-field--number"
                                        onKeyDown={(ev) => {
                                            if (
                                                ev.key === "e" ||
                                                ev.key === "E"
                                            ) {
                                                ev.preventDefault();
                                            }
                                        }}
                                        onPaste={(ev) => {
                                            ev.preventDefault();
                                            const text =
                                                ev.clipboardData?.getData(
                                                    "text"
                                                ) ?? "";
                                            const t = ev.currentTarget;
                                            const start =
                                                t.selectionStart ?? 0;
                                            const end = t.selectionEnd ?? 0;
                                            const next =
                                                sanitizeEmbedNumberFieldValue(
                                                    t.value.slice(0, start) +
                                                        text +
                                                        t.value.slice(end)
                                                );
                                            t.value = next;
                                            t.setSelectionRange(
                                                next.length,
                                                next.length
                                            );
                                        }}
                                        onInput={(ev) => {
                                            const t = ev.currentTarget;
                                            const next =
                                                sanitizeEmbedNumberFieldValue(
                                                    t.value
                                                );
                                            if (next !== t.value) {
                                                t.value = next;
                                            }
                                        }}
                                    />
                                </label>
                            );
                        }

                        if (field.type === "checkbox") {
                            const options = (field.options || []).filter(
                                (o) => String(o).trim() !== ""
                            );
                            return (
                                <div
                                    key={key}
                                    className="claritick-ticket-embed-option-group claritick-ticket-embed-option-group--checkbox"
                                >
                                    <span>
                                        {field.label || field.name}
                                        {req ? <EmbedRequiredMark /> : null}
                                    </span>
                                    {options.length > 0 ? (
                                        <div
                                            className={embedChoiceOptionsLayoutClass(
                                                "claritick-ticket-embed-checkbox-list",
                                                field.options_layout
                                            )}
                                        >
                                            {options.map((o) => (
                                                <label
                                                    key={o}
                                                    className="claritick-ticket-embed-option-row claritick-ticket-embed-option-row--checkbox"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        name={`pv-${key}`}
                                                        value={o}
                                                        tabIndex={-1}
                                                        className="claritick-ticket-embed-field claritick-ticket-embed-field--checkbox"
                                                    />
                                                    <span>{o}</span>
                                                </label>
                                            ))}
                                        </div>
                                    ) : null}
                                </div>
                            );
                        }

                        if (field.type === "select") {
                            return (
                                <label
                                    key={key}
                                    className="claritick-ticket-embed-label"
                                >
                                    <span>
                                        {field.label || field.name}
                                        {req ? <EmbedRequiredMark /> : null}
                                    </span>
                                    <select
                                        className="claritick-ticket-embed-field claritick-ticket-embed-field--select"
                                        required={req}
                                        defaultValue={
                                            req
                                                ? (field.options || [])[0] ??
                                                  ""
                                                : ""
                                        }
                                    >
                                        {!req ? (
                                            <option value="">
                                                {selectEmptyOptionLabel(
                                                    field.placeholder
                                                )}
                                            </option>
                                        ) : null}
                                        {(field.options || []).map((o) => (
                                            <option key={o} value={o}>
                                                {o}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            );
                        }

                        if (field.type === "radio") {
                            return (
                                <div
                                    key={key}
                                    className="claritick-ticket-embed-option-group claritick-ticket-embed-option-group--radio"
                                >
                                    <span>
                                        {field.label || field.name}
                                        {req ? <EmbedRequiredMark /> : null}
                                    </span>
                                    <div
                                        className={embedChoiceOptionsLayoutClass(
                                            "claritick-ticket-embed-radio-pills",
                                            field.options_layout
                                        )}
                                    >
                                        {(field.options || []).map((o, oi) => (
                                            <label
                                                key={o}
                                                className="claritick-ticket-embed-radio-pill"
                                            >
                                                <input
                                                    type="radio"
                                                    name={`pv-${key}`}
                                                    defaultChecked={oi === 0}
                                                    required={req && oi === 0}
                                                    tabIndex={-1}
                                                    className="claritick-ticket-embed-field claritick-ticket-embed-field--radio"
                                                />
                                                <span>{o}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            );
                        }

                        return (
                            <label
                                key={key}
                                className="claritick-ticket-embed-label"
                            >
                                <span>
                                    {field.label || field.name}
                                    {req ? <EmbedRequiredMark /> : null}
                                </span>
                                <input
                                    type="text"
                                    placeholder={field.placeholder}
                                    required={req}
                                    className="claritick-ticket-embed-field claritick-ticket-embed-field--text"
                                />
                            </label>
                        );
                    })}

                    <button
                        type="button"
                        className="claritick-ticket-embed-submit embed-form-editor-preview-submit-btn"
                        title="Preview submit — validates required fields, nothing is sent"
                        onClick={handlePreviewSubmit}
                    >
                        {cfg.button_text || "Submit"}
                    </button>
                    {previewError ? (
                        <div
                            className="claritick-ticket-embed-status claritick-ticket-embed-status--error"
                            role="alert"
                        >
                            {previewError}
                        </div>
                    ) : hint ? (
                        <div className="claritick-ticket-embed-status embed-form-editor-preview__hint">
                            {hint}
                        </div>
                    ) : null}
                </form>
                    </div>
                    {renderPreviewSuccessStage(showSidebarSuccess)}
                    </div>
                </div>
            </div>
            {allowViewToggle ? (
                <div
                    className="embed-form-editor-preview-view-toggle embed-forms-segment-toggle d-flex flex-nowrap gap-2"
                    role="group"
                    aria-label="Preview which screen to style"
                >
                    <Button
                        type="button"
                        variant={
                            designPreviewView === "form"
                                ? EmbedFormBtn.primary
                                : EmbedFormBtn.secondary
                        }
                        className="embed-forms-segment-toggle__btn"
                        aria-pressed={designPreviewView === "form"}
                        onClick={() => setDesignPreviewView("form")}
                    >
                        Form
                    </Button>
                    <Button
                        type="button"
                        variant={
                            designPreviewView === "success"
                                ? EmbedFormBtn.primary
                                : EmbedFormBtn.secondary
                        }
                        className="embed-forms-segment-toggle__btn"
                        aria-pressed={designPreviewView === "success"}
                        onClick={() => setDesignPreviewView("success")}
                    >
                        After submit
                    </Button>
                </div>
            ) : null}
        </>
    );

    const previewHostShell = (
        <div
            className="claritick-ticket-embed-root embed-form-editor-preview-host"
            style={{
                ["--embed-preview-hint-color"]: textHex,
            }}
        >
            {previewHostInner}
        </div>
    );

    const floatingPanel = (
        <div
            className="claritick-ticket-embed-floating-panel"
            aria-hidden={floatingPreviewOpen ? "false" : "true"}
        >
            <div className="embed-form-editor-preview-floating-toolbar">
                {minWidthNoticeButton}
                <button
                    type="button"
                    className="claritick-ticket-embed-floating-close"
                    aria-label="Close form preview"
                    onClick={() => setFloatingPreviewOpen(false)}
                >
                    ×
                </button>
            </div>
            {previewHostShell}
        </div>
    );

    const floatingLauncher = (
        <button
            type="button"
            className="claritick-ticket-embed-launcher"
            aria-label="Open form preview"
            aria-expanded={floatingPreviewOpen}
            onClick={() => setFloatingPreviewOpen((open) => !open)}
        >
            <span
                dangerouslySetInnerHTML={{
                    __html: embedLauncherIconHtml(assetOrigin),
                }}
            />
            <span className="claritick-ticket-embed-launcher__tooltip">
                {embedLauncherHoverText(cfg)}
            </span>
        </button>
    );

    const successModalPortal =
        showSuccessModal && typeof document !== "undefined"
            ? createPortal(
                  <div
                      className="embed-form-editor-preview-modal"
                      role="presentation"
                  >
                      <button
                          type="button"
                          className="embed-form-editor-preview-modal__backdrop"
                          aria-label="Close success preview"
                          onClick={dismissPreviewSuccess}
                      />
                      <div
                          className="embed-form-editor-preview-modal__dialog"
                          role="dialog"
                          aria-modal="true"
                          aria-label="After submit preview"
                          style={{ backgroundColor: bg }}
                      >
                          <button
                              type="button"
                              className="embed-form-editor-preview-modal__close"
                              aria-label="Close success preview"
                              onClick={dismissPreviewSuccess}
                          >
                              ×
                          </button>
                          <style type="text/css">{modalBaseCss}</style>
                          {customCssRaw.trim() ? (
                              <style type="text/css">{customCssRaw}</style>
                          ) : null}
                          <div
                              className="embed-form-editor-preview-modal__embed claritick-ticket-embed-root"
                              data-claritick-instance={modalInstanceId}
                          >
                              <div className="embed-form-editor-preview__frame claritick-ticket-embed-card is-success-state">
                                  <div className="claritick-ticket-embed-stage">
                                      <div className="claritick-ticket-embed-stage__body">
                                          {renderPreviewSuccessStage(true)}
                                      </div>
                                  </div>
                              </div>
                          </div>
                      </div>
                  </div>,
                  document.body
              )
            : null;

    return (
        <>
            <div
                ref={measureRef}
                className={floatingMeasureClass}
                style={floatingMeasureStyle}
            >
                {isFloatingPreview ? (
                    <div
                        className={floatingShellClass}
                        data-claritick-instance={instanceId}
                    >
                        {floatingPanel}
                        {floatingLauncher}
                    </div>
                ) : (
                    <div data-claritick-instance={instanceId}>
                        {previewHostShell}
                    </div>
                )}
            </div>
            {successModalPortal}
        </>
    );
}

const EmbedFormEditorPreview = React.memo(EmbedFormEditorPreviewImpl);
export default EmbedFormEditorPreview;
